/**
 * Notifications API Service
 *
 * Handles all notification-related API calls:
 * - Get paginated notifications
 * - Get unread count
 * - Mark notifications as read
 * - Delete notifications
 */

import { get, post } from './client';

const BASE_ENDPOINT = 'pegasus/visionary/notifications';

export const notificationsApi = {
    /**
     * Get paginated notifications for the authenticated user
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.page_size - Items per page (default: 20, max: 100)
     * @param {string} params.category - Filter by category (PAYMENT, LEASE, MAINTENANCE, TENANT, SYSTEM)
     * @param {boolean} params.is_read - Filter by read status
     * @returns {Promise<Object>} Paginated notifications response
     */
    getNotifications: async (params = {}) => {
        const { page = 1, page_size = 20, category, is_read } = params;
        const body = { page, page_size };

        if (category) body.category = category;
        if (typeof is_read === 'boolean') body.is_read = is_read;

        return post(`${BASE_ENDPOINT}/get`, body);
    },

    /**
     * Get unread notification count for badge display
     * Lightweight endpoint optimized for frequent polling
     * @returns {Promise<Object>} Unread count response with by_category breakdown
     */
    getUnreadCount: async () => {
        return get(`${BASE_ENDPOINT}/unread-count`);
    },

    /**
     * Mark a single notification as read
     * @param {number} notificationId - ID of the notification to mark as read
     * @returns {Promise<Object>} Success/error response
     */
    markAsRead: async (notificationId) => {
        return post(`${BASE_ENDPOINT}/mark-read`, {
            notification_id: notificationId,
        });
    },

    /**
     * Mark all notifications (or all in a category) as read
     * @param {string} category - Optional category to filter (omit for all categories)
     * @returns {Promise<Object>} Response with count of marked notifications
     */
    markAllAsRead: async (category) => {
        const body = category ? { category } : {};
        return post(`${BASE_ENDPOINT}/mark-all-read`, body);
    },

    /**
     * Delete/dismiss a notification
     * @param {number} notificationId - ID of the notification to delete
     * @returns {Promise<Object>} Success/error response
     */
    deleteNotification: async (notificationId) => {
        return post(`${BASE_ENDPOINT}/delete`, {
            notification_id: notificationId,
        });
    },
};

export default notificationsApi;
